// ============================================================================
// Equipment types — mirror of cr.or_equipment_catalog
// ----------------------------------------------------------------------------
// All 43 columns represented. Snake_case to match the database directly so
// supabase-js queries don't need translation; we adapt at the component layer
// where needed.
// ============================================================================

export type EquipmentStatus =
    | 'available'
    | 'in-use'
    | 'soiled'
    | 'awaiting-decontamination'
    | 'in-decontamination'
    | 'clean-and-inspected'
    | 'in-sterilization'
    | 'sterile-ready'
    | 'maintenance'
    | 'out-of-service'
    | 'retired';

export interface Equipment {
    equipment_id: number;
    org_id: string;

    // Identity
    equipment_name: string;
    equipment_category: string;
    equipment_subcategory: string | null;
    manufacturer: string | null;
    model_number: string | null;
    serial_number: string | null;
    asset_tag: string | null;
    description: string | null;

    // Sterilization
    reusable: boolean;
    sterilization_required: boolean | null;
    sterilization_method: string | null;

    // Inventory
    quantity_on_hand: number;
    quantity_in_use: number;
    quantity_in_sterilization: number;
    minimum_quantity: number;
    maximum_quantity: number | null;
    primary_storage_location: string | null;
    current_location: string | null;

    // Lifecycle
    acquisition_date: string | null;
    purchase_price: number | null;
    vendor: string | null;
    warranty_expiration: string | null;

    // Maintenance
    requires_maintenance: boolean | null;
    maintenance_interval_days: number | null;
    last_maintenance_date: string | null;
    next_maintenance_due: string | null;
    maintenance_notes: string | null;

    // Compliance
    fda_approved: boolean | null;
    fda_clearance_number: string | null;
    requires_inspection: boolean | null;
    last_inspection_date: string | null;
    inspection_due_date: string | null;

    // Status
    status: EquipmentStatus;
    out_of_service: boolean | null;
    out_of_service_reason: string | null;

    // Billing
    billable: boolean | null;
    billing_code: string | null;
    billing_price: number | null;

    // Lifecycle flag
    is_active: boolean | null;

    created_at: string;
    updated_at: string;
    updated_by: string | null;
}

// Categories used in the seed data — used for filter chips
export const EQUIPMENT_CATEGORIES = [
    'Anesthesia',
    'Electrosurgery',
    'Endoscopy',
    'Monitoring',
    'Surgical Instruments',
    'Positioning',
    'Sterilization',
    'Imaging',
    'Lighting & Power'
] as const;

// Status display order for filter UI — follows the AAMI ST79 cycle, then off-cycle states
export const EQUIPMENT_STATUSES: EquipmentStatus[] = [
    'available',
    'in-use',
    'soiled',
    'awaiting-decontamination',
    'in-decontamination',
    'clean-and-inspected',
    'in-sterilization',
    'sterile-ready',
    'maintenance',
    'out-of-service',
    'retired'
];

// Helpers for derived state — kept here so any consumer can use them
// rather than duplicating the logic.

export function isAtParLevel(eq: Equipment): boolean {
    return eq.quantity_on_hand <= eq.minimum_quantity;
}

export function isMaintenanceOverdue(eq: Equipment): boolean {
    if (!eq.next_maintenance_due) return false;
    return new Date(eq.next_maintenance_due) < new Date();
}

export function isMaintenanceDueSoon(eq: Equipment, days = 7): boolean {
    if (!eq.next_maintenance_due) return false;
    const due = new Date(eq.next_maintenance_due);
    const now = new Date();
    if (due < now) return false;  // already overdue, different state
    const diff = (due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return diff <= days;
}

export function isInspectionOverdue(eq: Equipment): boolean {
    if (!eq.inspection_due_date) return false;
    return new Date(eq.inspection_due_date) < new Date();
}

export function isInspectionDueSoon(eq: Equipment, days = 14): boolean {
    if (!eq.inspection_due_date) return false;
    const due = new Date(eq.inspection_due_date);
    const now = new Date();
    if (due < now) return false;
    const diff = (due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return diff <= days;
}

// Days until next maintenance — negative if overdue
export function daysUntilMaintenance(eq: Equipment): number | null {
    if (!eq.next_maintenance_due) return null;
    const due = new Date(eq.next_maintenance_due);
    const now = new Date();
    return Math.floor((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}
